import { Dispatch, SetStateAction, useMemo } from "react";

import { Button } from "antd";
import useTranslation from "next-translate/useTranslation";
import { FiX } from "react-icons/fi";

import StaticIntlTranslator from "shared/components/atoms/StaticIntlTranslator";
import { EntityProps } from "shared/components/organisms/EnrollmentItems/@types/enrollments";
import switchContext from "shared/components/organisms/EnrollmentItems/utils/switchContext";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import staticIntlTranslatorToString from "shared/utils/staticIntlTranslatorToString";

import BundleCard from "./components/BundleCard";
import FeaturesList from "./components/FeaturesList";
import Warranty from "./components/Warranty";
import { BundlesOverlayContainer } from "./styles";

const BundlesOverlay = ({
  entity,
  bundles,
  context,
  handlePurchase,
  individualSale,
  showBundlesOverlay,
  setShowBundlesOverlay,
}: BundlesOverlayProps) => {
  const { lang } = useTranslation();
  const { state, setState } = useGlobalContext();

  const themeBuyButtonsOption =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsOption;

  const themeBuyButtonsColor =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsColor;

  const publishedPlansList = entity?.plans?.filter(
    (plan) => plan?.status === "published",
  );

  const hasPublisedPlans = (plans) => {
    const publishedPlans = plans?.some((plan) => plan?.status === "published");

    return publishedPlans;
  };

  const eligibleBundles = bundles?.filter(
    (bundle) =>
      bundle?.status === "published" &&
      bundle?.access_type === "paid" &&
      (bundle?.individual_sale === true ||
        hasPublisedPlans(bundle?.plans) === true),
  );

  const lowestPriceMainProduct = useMemo(() => {
    const priceInfoPublishedPlansList = publishedPlansList?.map(
      (plan) => plan?.price_info,
    );

    const priceValuePublishedPlansList = publishedPlansList?.map(
      (plan) => plan?.price_info?.value,
    );

    const priceInfoList = individualSale
      ? [entity?.price_info, ...priceInfoPublishedPlansList]
      : priceInfoPublishedPlansList;

    const priceValueList = individualSale
      ? [entity?.price_info?.value, ...priceValuePublishedPlansList]
      : priceValuePublishedPlansList;

    const lowestPriceValue = Math.min(...priceValueList);

    const lowestPriceInfo = priceInfoList?.find(
      (item) => item?.value === lowestPriceValue,
    );

    const formattedPrice = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 2,
    }).format(lowestPriceInfo?.value);

    return { lowestPriceInfo, formattedPrice };
  }, [individualSale, entity?.price_info, entity?.plans]);

  const handleOpenPurchaseOptions = () => {
    setState((prev) => ({
      ...prev,
      enrollments: {
        paymentOptions: {
          entity,
          context,
          handlePurchase,
          show: true,
        },
      },
    }));
  };

  const verifyContextAndGoToCheckout = () => {
    const { entityType, paramType } = switchContext({ context });

    handlePurchase(entityType, paramType, entity);
  };

  return (
    <BundlesOverlayContainer
      data-testid="modalBundleOverlay"
      centered
      width="fit-content"
      title={null}
      footer={null}
      visible={showBundlesOverlay}
      closeIcon={<FiX size={34} color="#000" />}
      customButtonColor={themeBuyButtonsColor}
      themeBuyButtonsOption={themeBuyButtonsOption}
      onCancel={() => setShowBundlesOverlay(false)}
      maskStyle={{
        background: "rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(15px)",
      }}
    >
      <h2>{staticIntlTranslatorToString(entity.title, lang)}</h2>

      {!state.isMobileAppClient &&
        (individualSale || hasPublisedPlans(entity?.plans) ? (
          <>
            <div className="individual-sale-card">
              {individualSale && !hasPublisedPlans(entity?.plans) && (
                <>
                  <h3>
                    <StaticIntlTranslator id="common:buyFor" />
                    &nbsp;
                    <span className="price">
                      {entity.price.replace("R$", "R$ ")}
                    </span>
                  </h3>

                  <Button
                    className="buy-btn"
                    onClick={() => {
                      verifyContextAndGoToCheckout();
                    }}
                  >
                    <StaticIntlTranslator id="common:buyNow" />
                  </Button>

                  <p>
                    <StaticIntlTranslator id="common:singlePayment" />
                  </p>
                </>
              )}

              {!individualSale && hasPublisedPlans(entity?.plans) && (
                <>
                  <h3>
                    {publishedPlansList?.length <= 1 ? (
                      <StaticIntlTranslator id="common:subscribeFor" />
                    ) : (
                      <StaticIntlTranslator id="common:signBy" />
                    )}
                    &nbsp;
                    <span className="price">
                      {lowestPriceMainProduct?.formattedPrice.replace(
                        "R$",
                        "R$ ",
                      )}
                    </span>
                  </h3>

                  <Button
                    type="primary"
                    className="buy-btn"
                    onClick={() => {
                      if (publishedPlansList?.length <= 1) {
                        verifyContextAndGoToCheckout();
                      } else {
                        handleOpenPurchaseOptions();
                      }
                    }}
                  >
                    {publishedPlansList?.length <= 1 ? (
                      <StaticIntlTranslator id="common:subscribeNow" />
                    ) : (
                      <StaticIntlTranslator id="common:choosePlan" />
                    )}
                  </Button>
                </>
              )}

              {individualSale && hasPublisedPlans(entity?.plans) && (
                <>
                  <h3>
                    <StaticIntlTranslator id="common:signBy" />
                    &nbsp;
                    <span className="price">
                      {lowestPriceMainProduct?.formattedPrice.replace(
                        "R$",
                        "R$ ",
                      )}
                    </span>
                  </h3>

                  <Button
                    type="primary"
                    className="buy-btn"
                    onClick={handleOpenPurchaseOptions}
                  >
                    <StaticIntlTranslator id="common:buyOrSubscribe" />
                  </Button>
                </>
              )}

              {entity?.features?.length > 0 && (
                <FeaturesList features={entity.features} />
              )}
            </div>

            {eligibleBundles.length > 0 && (
              <h3>
                <StaticIntlTranslator id="common:orSignPlan" />
              </h3>
            )}
          </>
        ) : (
          <h3>
            {eligibleBundles?.length > 1 ? (
              <StaticIntlTranslator id="common:chooseBundle" />
            ) : (
              <StaticIntlTranslator id="common:availableInTheBundle" />
            )}
          </h3>
        ))}

      <div className="plans-container">
        {eligibleBundles.map((bundle) => {
          if (eligibleBundles.length)
            return (
              <BundleCard
                bundle={bundle}
                key={bundle.id}
                handlePurchase={handlePurchase}
              />
            );

          return null;
        })}
      </div>

      <Warranty />
    </BundlesOverlayContainer>
  );
};

export interface BundlesOverlayProps {
  bundles: any[];
  entity: EntityProps;
  context: string;
  handlePurchase: (
    entityType?: string,
    paramType?: string,
    entity?: EntityProps,
  ) => void;
  individualSale: boolean;
  showBundlesOverlay: boolean;
  setShowBundlesOverlay: Dispatch<SetStateAction<boolean>>;
}

export default BundlesOverlay;
